import {BrowserRouter, Route, Routes} from "react-router-dom";
import Home from "./Home";
import Dashboard from "./dashboard/page";
import CLoader from "@/components/CLoader";
import IsAllowed from "@/components/IsAllowed";
import LoaderProvider from "@/contexts/LoaderProvider";
import ErrorProvider from "@/contexts/ErrorProvider";
import ShowProvider from "@/contexts/ShowProvider";
import FeatureProvider from "@/contexts/FeatureProvider";
import InvestDialogProvider from "@/contexts/InvestDialogProvider";
import MobileMenuProvider from "@/contexts/MobileMenuProvider";
import {SimpleTourProvider} from "@/contexts/SimpleTourProvider";
import IntercomProvider from "@/components/IntercomProvider";
import SimpleTour from "@/components/SimpleTour";
import {useSimpleTour} from "@/contexts/SimpleTourProvider";
import Architecture from "./architecture/page";
import Settings from "@/settings/page";
import PricingPlans from "./pricing/Page";
import PaymentResultPage from "./payment/Page";
import Create from "./architecture/create/page";
import Specs from "./architecture/specs/page";
import Generate from "./architecture/generate/page";
import Preview from "./architecture/preview/page";
import Shreds from "./architecture/project/services/page";
import StepController from "./architecture/project/services/generate/page";
import Marketplace from "./marketplace/page";
import Collaborate from "./collaborate/page";
import Analytics from "./analytics/page";
import Privacy from "./privacy/page";
import ForbiddenPage from "./forbidden/page";
import Maintenance from "./maintenance/page";
import AuthGuard from "./AuthGuard";
import { AuthFormProvider } from "@/contexts/AuthFormProvider";
import { PendingProjectProvider } from "@/contexts/PendingProjectProvider";
import AuthTopBar from "@/components/auth/AuthTopBar";

// Component wrapper that includes Intercom for authenticated routes
const AuthGuardWithIntercom = ({Component, ...rest}: any) => (
    <IntercomProvider>
      <AuthGuard Component={Component} {...rest} />
    </IntercomProvider>
);

// Tour wrapper component to access tour context
const TourWrapper = () => {
  const {isActive, dismissedTours, stopTour, dismissCurrentTour} = useSimpleTour();

  return (
      <SimpleTour
          isActive={isActive}
          dismissedTours={dismissedTours}
          onClose={stopTour} // This completely turns off the guide
          onDismiss={dismissCurrentTour} // This just dismisses current tour
      />
  );
};

function App() {
  return (
      <LoaderProvider>
        <ErrorProvider>
          <ShowProvider>
            <FeatureProvider>
              <InvestDialogProvider>
                <MobileMenuProvider>
                  <BrowserRouter
                      basename="/"
                      future={{v7_startTransition: true}}
                  >
                    <PendingProjectProvider>
                      <AuthFormProvider>
                        <SimpleTourProvider>
                      <Routes>
                        <Route path="/" Component={Home}/>
                        <Route path="/signup" Component={Maintenance}/>
                        <Route path="/signin" Component={Maintenance}/>
                        <Route
                            path="/dashboard"
                            element={<AuthGuardWithIntercom Component={Dashboard}/>}
                        />
                        <Route
                            path="/architecture"
                            element={<AuthGuardWithIntercom Component={Architecture}/>}
                        />
                        <Route
                            path="/settings"
                            element={<AuthGuardWithIntercom Component={Settings}/>}
                        />
                        <Route
                            path="/pricing/plans"
                            element={<AuthGuardWithIntercom Component={PricingPlans}/>}
                        />
                        <Route
                            path="/payment/success"
                            element={<AuthGuardWithIntercom Component={PaymentResultPage} mode="success"/>}
                        />
                        <Route
                            path="/payment/cancel"
                            element={<AuthGuardWithIntercom Component={PaymentResultPage} mode="cancel"/>}
                        />
                        <Route path="/architecture/create" element={<AuthGuardWithIntercom Component={Create}/>}/>
                        <Route path="/architecture/specs/:id" element={<AuthGuardWithIntercom Component={Specs}/>}/>
                        <Route
                            path="/architecture/generate/:id"
                            element={<AuthGuardWithIntercom Component={Generate}/>}
                        />
                        <Route
                            path="/architecture/preview/:id"
                            element={<AuthGuardWithIntercom Component={Preview}/>}
                        />
                        <Route
                            path="/architecture/project/:id/services/:serviceId"
                            element={<AuthGuardWithIntercom Component={Shreds}/>}
                        />
                        <Route
                            path="/architecture/project/:projectId/services/:serviceId/create"
                            element={<AuthGuardWithIntercom Component={StepController}/>}
                        />
                        <Route
                            path="/architecture/project/:projectId/shred/:id"
                            element={<AuthGuardWithIntercom Component={StepController}/>}
                        />
                        <Route path="/marketplace" element={<AuthGuardWithIntercom Component={Marketplace}/>}/>
                        <Route path="/collaborate" element={<AuthGuardWithIntercom Component={Collaborate}/>}/>
                        <Route path="/analytics" element={<AuthGuardWithIntercom Component={Analytics}/>}/>
                        <Route path="/login" Component={Maintenance}/>
                        <Route path="/privacy" Component={Privacy}/>
                        <Route path="/forbidden" Component={ForbiddenPage}/>
                      </Routes>

                      {/* Global Tour Component */}
                      <TourWrapper/>

                      <CLoader/>
                      <IsAllowed/>
                      
                      {/* Global Auth Modal */}
                      <AuthTopBar/>
                        </SimpleTourProvider>
                      </AuthFormProvider>
                    </PendingProjectProvider>
                  </BrowserRouter>
                </MobileMenuProvider>
              </InvestDialogProvider>
            </FeatureProvider>
          </ShowProvider>
        </ErrorProvider>
      </LoaderProvider>
  );
}

export default App;